import { baseQueryArgs, baseExtraOptions, rsosApi } from '../rsosApi';
import { csvToMessageRecords } from './csvToMessageRecords';

const TF911_REPORT_TEXT = 'Text_from_911_Report_Download';

const getTextFrom911BaseEndpoint = orgName => {
  return `v1/tf911/psap/${orgName}`;
};

export const THIRTY_DAYS = 1000 * 60 * 60 * 24 * 30;
export const TWO_HOURS = 1000 * 60 * 60 * 2;

/**
 * Extends the rsosApi for text-from-911 endpoints.
 * Authorization header must be manually included to tell RTK which token to use
 * otherwise it will use the access token
 */
export const textFrom911Api = rsosApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * Fetches a phone number's ongoing conversation metadata.
     * Returns 204 if none exists
     * @param {String} callerID - phone number
     * @param {String} orgName - Name of authority the user belongs to
     */
    getConversationMetadata: builder.query({
      query: ({ callerID, orgName }) => ({
        ...baseQueryArgs,
        url: `${getTextFrom911BaseEndpoint(orgName)}/conversation/+${callerID}`,
        method: 'GET',
      }),
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Starts a conversation with the given phone number using `startMessage`
     * @param {String} callerID - phone number
     * @param {String} orgName - Name of authority the user belongs to
     * @param {String} startMessage - Message to send to the phone number
     */
    // TODO: should be builder.mutation
    createConversation: builder.query({
      query: ({ callerID, orgName, startMessage, languageCode }) => ({
        ...baseQueryArgs,
        url: `${getTextFrom911BaseEndpoint(orgName)}/conversation/+${callerID}`,
        method: 'POST',
        body: JSON.stringify({
          start_message: startMessage,
          ...(languageCode ? { language_code: languageCode } : {}),
        }),
      }),
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Ends a conversation with the given phone number
     * @param {String} callerID - phone number
     * @param {String} orgName - Name of authority the user belongs to
     */
    endConversation: builder.query({
      query: ({ callerID, orgName }) => ({
        ...baseQueryArgs,
        url: `${getTextFrom911BaseEndpoint(orgName)}/conversation/+${callerID}`,
        method: 'PUT',
      }),
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Fetches conversations in a date range. Used with the corresponding TF911
     * admin page
     * @param {String} orgName - Name of authority the user belongs to
     * @param {Integer} [startTime=30 days ago] - UTC milliseconds for start
     * date of conversations.
     * @param {Integer} [endTime=current time] - UTC milliseconds for end date
     * of conversations
     */
    getHistoricConversations: builder.query({
      query: ({
        orgName,
        startTime = new Date().getTime() - THIRTY_DAYS,
        endTime = new Date().getTime(),
        status = '',
        sessionTypes = '',
      }) => ({
        ...baseQueryArgs,
        url: `${getTextFrom911BaseEndpoint(
          orgName,
        )}/conversations?start_ms=${startTime}&end_ms=${endTime}${status ? `&status=${status}` : ''}${sessionTypes ? `&session_types=${sessionTypes}` : ''}`,
        method: 'GET',
      }),
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * @param {Array} conversationIds - List of conversation IDs to download
     * @param {String} orgName - Name of authority the user belongs to
     */
    downloadConversations: builder.mutation({
      query: ({ conversationIds, orgName, psapsDisplayName }) => ({
        ...baseQueryArgs,
        url: `${getTextFrom911BaseEndpoint(
          orgName,
        )}/conversation-history/download`,
        method: 'POST',
        body: {
          conversations: conversationIds,
        },
        responseHandler: async response => {
          if (!response.ok) {
            return response.text();
          }

          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(
            new Blob([blob], { type: 'text/csv' }),
          );

          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = `${psapsDisplayName}_${TF911_REPORT_TEXT}_${Date.now()}.csv`;
          a.click();

          // Clean up
          a.remove();
          window.URL.revokeObjectURL(blobUrl);
        },
        cache: 'no-cache',
      }),
    }),
    /**
     * Parses the CSV from `/conversation-history/download` into structured
     * message records instead of triggering a file download. Reuses the
     * exact endpoint (and therefore the auth + scoping) of
     * `downloadConversations` so the FE can build a search index without
     * any backend work.
     *
     * Caveats this inherits from the download endpoint:
     *   - Returns 403 if any requested id isn't an INACTIVE TF911 (or
     *     active text) session for the caller's authority. Callers should
     *     fetch one id at a time and skip 403s, otherwise a single bad id
     *     poisons the whole batch.
     *   - Cannot fetch live/active TF911 conversations (the BE's session
     *     lookup hardcodes `status=INACTIVE` for TF911). For active call
     *     queue coverage, the BE would need a sibling endpoint without
     *     that filter.
     *   - Cannot fetch collaborator/agency-share rooms (no TF911 session
     *     row exists for them).
     *
     * Resolves to `{ messages: Array<{ id, cleanedConversationId,
     * senderId, text, createdAt }> }`. The `cleanedConversationId` field
     * has no `tf911.` prefix (the BE strips it for CSV readability), so
     * the caller is responsible for mapping back to whichever id it sent.
     *
     * @param {String} conversationId - Single conversation id to fetch.
     * @param {String} orgName - Name of authority the user belongs to.
     */
    getConversationMessages: builder.mutation({
      query: ({ conversationId, orgName }) => ({
        ...baseQueryArgs,
        url: `${getTextFrom911BaseEndpoint(
          orgName,
        )}/conversation-history/download`,
        method: 'POST',
        body: {
          conversations: [conversationId],
        },
        responseHandler: async response => {
          // Non-2xx (most commonly the BE's 403 for active / non-owned
          // conversations) flows into RTK Query's `error.data` — return
          // the raw text so callers can log it if they want, but don't
          // try to parse it as CSV.
          if (!response.ok) {
            return await response.text();
          }
          const csvText = await response.text();
          return { messages: csvToMessageRecords(csvText) };
        },
        cache: 'no-cache',
      }),
      extraOptions: {
        ...baseExtraOptions,
        // Each failure (403, etc.) is expected and surfaced to the
        // caller; retrying won't help and just doubles the load on a
        // batch that already does N parallel requests.
        maxRetries: 0,
      },
    }),
    createConversationOutboundSMS: builder.mutation({
      query: ({ callerID, orgName, message, languageCode }) => ({
        ...baseQueryArgs,
        url: `${getTextFrom911BaseEndpoint(orgName)}/outbound-sms`,
        method: 'POST',
        body: JSON.stringify({
          caller_id: `+${callerID}`,
          message,
          ...(languageCode ? { language_code: languageCode } : {}),
        }),
      }),
      extraOptions: {
        ...baseExtraOptions,
        maxRetries: 0, // 409 error statuses trigger retry logic. explicitly do not retry
      },
    }),
    // exactly the same as outboundSMS mutation
    createConversationOutboundAgero: builder.mutation({
      query: ({ callerID, orgName, message, languageCode }) => ({
        ...baseQueryArgs,
        url: `${getTextFrom911BaseEndpoint(orgName)}/outbound-sms`,
        method: 'POST',
        body: JSON.stringify({
          caller_id: `+${callerID}`,
          message,
          ...(languageCode ? { language_code: languageCode } : {}),
          // custom, TODO: look into passing in custom field
        }),
      }),
      extraOptions: {
        ...baseExtraOptions,
        maxRetries: 0, // 409 error statuses trigger retry logic. explicitly do not retry
      },
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  useGetConversationMetadataQuery,
  useLazyGetConversationMetadataQuery,
  useCreateConversationQuery,
  useLazyCreateConversationQuery,
  useEndConversationQuery,
  useLazyEndConversationQuery,
  useGetHistoricConversationsQuery,
  useLazyGetHistoricConversationsQuery,
  useDownloadConversationsMutation,
  useGetConversationMessagesMutation,
  useCreateConversationOutboundSMSMutation,
  useCreateConversationOutboundAgeroMutation,
} = textFrom911Api;
